/* ── THE REVIEW STARS FOR A CARD BUILT IN THE BROWSER ────────────────────────
 * window.RugLoox — ONE definition, used by the rug finder and the favourites
 * page. Every OTHER card surface on this site is rendered by Liquid and gets
 * its stars from `snippets/rug-loox-stars.liquid`, which reads the count
 * straight off the product's metafields and costs no request at all. This file
 * exists only for the two surfaces that have no product object to read: the
 * finder builds its cards from a 27 KB index, and the favourites page builds
 * its cards from a list held in localStorage.
 *
 * IT IS ONE FILE BECAUSE IT WAS ONE FILE'S WORTH OF THINKING. The finder had
 * all of this inline; the favourites page needed the identical debounce, the
 * identical per-view memo, the identical URL construction and the identical
 * "no count, no stars" rule. CLAUDE.md records exactly what writing that twice
 * costs — `rug-cart.js` is the same shape, one inert dispatch written twice, so
 * one of them being wrong meant both were. Do not add a third copy.
 *
 * IT IS THE PRODUCT PAGE'S OWN WIDGET, NOT A SECOND ONE. Same class, same two
 * data attributes, same `data-content-size` — the Loox loader is on every page
 * of this store through an app embed, and its script runs a MutationObserver
 * over `document` with `{subtree:true, childList:true}`, so a `.loox-rating`
 * appended here is found and drawn with no call from us. Verified live rather
 * than assumed: six widgets injected into a collection page all came back
 * carrying `data-rating-upgraded="1"` and their own stars.
 *
 * NOTHING ABOUT IT CAN COST A PAGE ANYTHING. No Loox on the page, no call. A
 * refused or malformed answer leaves the widget without a count, and a widget
 * without a count is never drawn — the caller's slot collapses and the card is
 * exactly as it was before this existed.
 *
 * NO NUMBER IS EVER HELD IN THIS FILE. A rating moves every time somebody
 * leaves one; the moment this file carries a copy it carries a stale one.
 * `KNOWN` below is the answer to a request made in THIS page view, discarded
 * with the page — a per-view memo of what the server just said, not a store. */
(function () {
  'use strict';

  /* WHAT LOOX ANSWERED, FOR THE LIFE OF THE PAGE VIEW. `ASKED` stops one
   * product being asked for twice, and that is right — but on its own it also
   * meant a widget created LATER for a product the page had already asked
   * about (the finder's details popup) was never filled and rendered empty.
   * The answer is kept, so a second widget for a known product is filled the
   * moment it exists, with no second request. */
  var KNOWN = {};
  var ASKED = {};
  var SETTLED = {};
  var timer = null;

  /* THE IDS ARRIVE IN WAVES, SO THE DEBOUNCE HAS TO OUTLAST THE WAVES. Each
   * card learns its product id when its own `/products/<handle>.js` lookup
   * lands, and those land in bursts. Measured on the live finder at 60ms: two
   * waves, two requests. 400ms covers the spread of ten lookups on this
   * connection and still puts the stars up inside the same second.
   *
   * AND `ASKED` IS WHAT MAKES IT EXACTLY ONE REQUEST PER PRODUCT, not the
   * timer. A debounce that is slightly too short asks twice; a set of ids
   * already asked for cannot, whatever the timing does. Belt and braces,
   * because the timing is a property of the network and the set is a property
   * of the code. */
  var DEBOUNCE_MS = 400;

  /* ONE CALL SERVES THE WHOLE PAGE, WHATEVER IS ON IT. The scan is over
   * `document`, not a caller's root, so a page carrying two JS-built card
   * surfaces asks once for both rather than once each. It selects only widgets
   * that still need a count, so the Liquid-rendered cards — which already carry
   * one — are skipped and cost nothing. */
  function pending() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.loox-rating[data-id]:not([data-raters])'));
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; fill(); }, DEBOUNCE_MS);
  }

  /* ── THE RESERVED HEIGHT, AND WHY IT IS A CLASS RATHER THAN A RULE ────────
   * A Liquid card carries its count in the HTML, so `[data-raters]` reserves
   * its height at first paint and the stars arrive into a box that is already
   * the right size. A card built here does not have the count yet, so the
   * height is held open by `rz-stars--pending` from the moment the widget is
   * created until its answer lands.
   *
   * SETTLED MEANS SETTLED, EITHER WAY. `paint` takes the class off because
   * `[data-raters]` then holds the height; `giveUp` takes it off because there
   * is nothing to show — no reviews, a refused request, no Loox on the page —
   * and a permanent 22px hole under a card with no stars in it is the exact
   * thing "no stars, no gap" promises will not happen. */
  var PENDING = 'rz-stars--pending';

  function paint(el, x) {
    el.setAttribute('data-rating', x.rating);
    el.setAttribute('data-raters', x.raters);
    el.classList.remove(PENDING);
    /* LOOX DRAWS IT FROM HERE. Its observer upgrades any `.loox-rating`
     * carrying a count — the element gains `data-rating-upgraded="1"`, its
     * height and its own stars. We never draw a star ourselves. */
  }

  function giveUp(els) {
    els.forEach(function (el) { el.classList.remove(PENDING); });
  }

  /* ITS OWN BULK ENDPOINT IS THE ANSWER AND IT IS THE ONE LOOX ITSELF USES:
   * `/widget/<clientId>/ratings?products_ids=<csv>&h=<hash>` -> `{ success,
   * ratings: [{pid, rating, raters}] }`. Read out of the deployed script, not
   * guessed.
   *
   * EVERY PART OF THE URL COMES FROM THE PAGE. `LOOX.root`, `LOOX.clientId`
   * and `LOOX.hash` are read at call time — hardcoding the shop id or the hash
   * would be a copy of somebody else's configuration that goes stale
   * silently. */
  function fill() {
    var L = window.LOOX;
    /* NO LOOX ON THE PAGE IS A SETTLED ANSWER, NOT A WAIT. Leaving the widgets
     * pending would hold 22px open under every card for the life of the page
     * on the one occasion we already know nothing will ever fill it. */
    if (!L || !L.root || !L.clientId || !L.hash) { giveUp(pending()); return; }

    var want = [], seen = {};
    pending().forEach(function (w) {
      var id = w.getAttribute('data-id');
      if (!id) return;
      if (KNOWN[id]) { paint(w, KNOWN[id]); return; }
      /* ASKED AND SETTLED ARE DIFFERENT STATES AND CONFLATING THEM LEAKS A
       * HOLE. `ASKED` means a request is out; a widget created for that id
       * while it is in flight must keep its reserved height and wait for the
       * shared answer. `SETTLED` means the answer came back and there was no
       * rating in it — so a widget created later for the same id is given up
       * at once rather than reserving 22px for a request that will never be
       * made again. */
      if (SETTLED[id]) { giveUp([w]); return; }
      if (ASKED[id] || seen[id]) return;
      seen[id] = 1; want.push(id);
    });
    if (!want.length) return;
    want.forEach(function (id) { ASKED[id] = 1; });

    /* THE ANSWER IS APPLIED TO WHATEVER IS ON THE PAGE WHEN IT ARRIVES, not to
     * the elements captured when it was sent — a card can be built while the
     * request is out, and it should be filled by it rather than wait for a
     * second one. */
    function done() {
      want.forEach(function (id) { SETTLED[id] = 1; });
      pending().forEach(function (w) {
        var id = w.getAttribute('data-id');
        if (!id || !SETTLED[id]) return;
        if (KNOWN[id]) paint(w, KNOWN[id]); else giveUp([w]);
      });
    }

    fetch(L.root + '/widget/' + encodeURIComponent(L.clientId) + '/ratings?products_ids=' +
          encodeURIComponent(want.join(',')) + '&h=' + encodeURIComponent(L.hash))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && d.success && Array.isArray(d.ratings)) {
          d.ratings.forEach(function (x) {
            /* A PRODUCT WITH NO REVIEWS GETS NO STARS, NOT A ROW OF EMPTY
             * ONES. All 561 products in the catalogue carry a rating today —
             * measured 2026-09-07, min 25 raters, median 54 — so this is not a
             * case that fires. But a product exists before its first review
             * does, and a grey outline row under a rug nobody has reviewed is
             * worse than nothing at all. The reserved height comes off with
             * it, so the card closes up rather than keeping a hole for stars
             * that are not coming. */
            if (x && x.raters) KNOWN[String(x.pid)] = { rating: x.rating, raters: x.raters };
          });
        }
        done();
      })
      .catch(function () { done(); /* no stars, no gap, no error on the page */ });
  }

  window.RugLoox = {
    /* Returns a widget element for `pid`, or null when there is no id to build
     * one from — a caller with no id gets no element rather than an empty box.
     * A product already answered for in this page view is filled immediately
     * and costs no request; anything else joins the next debounced batch. */
    widget: function (pid, className) {
      if (!pid) return null;
      var id = String(pid);
      var d = document.createElement('div');
      d.className = 'loox-rating loox-widget rz-stars' + (className ? ' ' + className : '');
      d.setAttribute('data-id', id);
      /* THE SAME 13px EVERY OTHER SURFACE USES, so a card in the finder and a
       * card in a collection grid are the same object. `snippets/
       * rug-loox-stars.liquid` reserves the matching height. */
      d.setAttribute('data-content-size', '13');
      if (KNOWN[id]) { paint(d, KNOWN[id]); return d; }
      d.classList.add(PENDING);
      schedule();
      return d;
    },
    /* For a caller that has appended widgets some other way, or that has just
     * replaced a list. Idempotent: a widget already carrying a count is not
     * asked about again, and neither is an id already asked for. */
    scan: schedule
  };
})();
